import React, { useState, useEffect } from 'react';
import { getDefaultHistorySettings, getHistorySettings, updateHistorySetting } from '../services/historyService';
import { getDisplayErrorMessage } from '../services/apiError';
import ErrorMessage from './ErrorMessage';

const NumberInput = ({ field, onChange }) => {
  return (
    <input 
      type="number"
      value={field.value}
      onChange={(e) => {
        const value = e.target.value;
        onChange(value === '' ? '' : Number(value));
      }}
      className="w-full rounded p-1"
    />
  )
}

const StringInput = ({ field, onChange }) => {
  return (
    <input
      type="text"
      value={field.value}
      onChange={(e) => onChange(String(e.target.value))}
      className="w-full rounded p-1"
    />
  )
}

const BooleanInput = ({ field,onChange }) => {
  return (
    <input
      type="checkbox"
      checked={field.value}
      onChange={(e) => onChange(e.target.checked)}
      className="mr-2"
    />
  )
}

const DateInput = ({ field, onChange }) => {
  return (
    <input
      type="date"
      value={field.value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full rounded p-1"
    />
  )
}

const SelectInput = ({ field, onChange }) => {
  return (
    <select
      value={field.value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full rounded p-1"
    >
     {field.available_values.map(value => (
        <option key={value} value={value}>{value}</option>
      ))}
    </select> 
  )
}

const MultiSelectInput = ({ field, onChange }) => {
  const currentValue = Array.isArray(field.value) ? field.value : [];
  
  return (
    <select
      multiple
      value={currentValue}
      onChange={(e) => {
        const selected = Array.from(e.target.selectedOptions, option => option.value);
        onChange(selected);
      }}
      className="w-full rounded p-1 min-h-[80px]"
    >
      {field.available_values.map(value => (
        <option key={value} value={value}>{value}</option>
      ))}
    </select>
  );
};

const SettingField = ({ field, onChange }) => {
    const renderInput = () => {
      if (field.available_values && field.available_values.length > 0){
        if (field.type === 'multiselect') {
          return <MultiSelectInput field={field} onChange={onChange} />
        }
        return <SelectInput field={field} onChange={onChange} />
      }
      
      switch (field.type) {
        case 'number':
          return <NumberInput field={field} onChange={onChange} />
        case 'string':
          return <StringInput field={field} onChange={onChange} />
        case 'boolean':
          return <BooleanInput field={field} onChange={onChange} />
        case 'date':
          return <DateInput field={field} onChange={onChange} />
        default:
          return <StringInput field={field} onChange={onChange} />
      }
    };

    return (
      <div className="mb-4">
        <label className="font-medium">{field.label}</label>
        {field.description && (
          <p className="text-xs text-gray-500 italic mt-1">{field.description}</p>
        )}
        <div className="mt-2">
          {renderInput()}
        </div>
      </div>
    )
  }

const SettingsSection = ({ section, onFieldChange }) => {
  return (
    <div className="mb-6">
      {section.fields.map(field => (
        <SettingField 
          key={field.key}
          field={field}
          onChange={(value) => onFieldChange(field.key, value)}
        />
      ))}
    </div>
  )
}

const HistorySettingsPanel = ({ historyId, onSettingsChange }) => {
  const [sections, setSections] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        setIsLoading(true);
        if (historyId) {
          const response = await getHistorySettings(historyId);
          if (response) {
            setSections(response.sections);
          }
        } else {
          const response = await getDefaultHistorySettings();
          if (response) {
            setSections(response.sections);
          }
        }
      } catch (err) {
        setError(getDisplayErrorMessage(err));
      } finally {
        setIsLoading(false);
      }
    };

    fetchSettings();
  }, [historyId]);

  const handleSettingChange = async (key, value) => {
    setSections(prevSections => {
      const newSections = prevSections.map(section => {
        const newFields = section.fields.map(field => {
          if (field.key === key) {
            return { ...field, value: value };
          }
          return field;
        });

        return { ...section, fields: newFields };
      });

      return newSections;
    })

    if (historyId) {
      try {
        await updateHistorySetting(historyId, key, value);
      } catch (err) {
        setError(getDisplayErrorMessage(err));
      }
    }

    if (onSettingsChange) {
      onSettingsChange(key, value);
    }
  }

  return (
    <div className="w-full relative px-12 bg-gray-300 rounded shadow-lg">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full py-4 flex items-center justify-center gap-2"
      >
        <span className="font-medium">Settings</span>
        <svg 
          xmlns="http://www.w3.org/2000/svg" 
          fill="none" 
          viewBox="0 0 24 24" 
          strokeWidth="2" 
          stroke="currentColor" 
          className={`w-5 h-5 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5" />
        </svg>
      </button>

      {isExpanded && (
        <div className="py-6">
          <ErrorMessage message={error} onDismiss={() => setError(null)} positionClass="top-0 right-0 left-0" />

          {isLoading ? (
            <p className="text-center text-gray-500">Loading settings...</p>
          ) : (
            sections.map(section => (
              <SettingsSection 
                key={section.id}
                section={section}
                onFieldChange={handleSettingChange}
              />
            ))
          )}
        </div>
      )}
    </div>
  );
}

export default HistorySettingsPanel;